import { DBOfflineStore } from "./offline-store";
import { Unarray } from "./types";


export enum OfflineDBInstance {
  VOLATEQ_OFFLINE_DB = "volateqOfflineDB",
  DT = "DT",
}

export class OfflineDB {
  // All connections opened through create()/open(), per database. deleteDatabase()
  // must close them first, otherwise the browser blocks the deletion.
  private static openDBs: Record<string, OfflineDB[]> = {};

  constructor(
    public readonly dbInstance: OfflineDBInstance,
    public readonly db: IDBDatabase,
  ) {
    OfflineDB.openDBs[dbInstance] = [...(OfflineDB.openDBs[dbInstance] || []), this];
  }

  /**
   * @returns true if the database exists in the browser.
   */
  public static async exists(dbInstance: OfflineDBInstance): Promise<boolean> {
    const offDB = await OfflineDB.open(dbInstance);
    if (!offDB) {
      return false;
    }

    offDB.close();

    return true;
  }

  /**
   * Deletes the database, closing all connections opened through this class before.
   * Resolves also if the database does not exist.
   */
  public static deleteDatabase(dbInstance: OfflineDBInstance): Promise<void> {
    for (const offDB of OfflineDB.openDBs[dbInstance] || []) {
      offDB.close();
    }

    return new Promise<void>((resolve, reject) => {
      const request = window.indexedDB.deleteDatabase(dbInstance);
      request.onerror = (e) => {
        console.error(e);

        reject(e);
      };
      request.onblocked = (e) => {
        // Another tab still holds a connection. The deletion stays queued by the
        // browser and completes as soon as that connection closes (the other tab
        // notices the logout via the cross-tab storage listeners and reloads).
        console.warn(`Deleting database ${dbInstance} is blocked by another connection. ` +
          "The deletion will complete as soon as the last connection closes.", e);

        resolve();
      };
      request.onsuccess = () => {
        resolve();
      };
    });
  }

  private close() {
    this.db.close();

    OfflineDB.openDBs[this.dbInstance] = (OfflineDB.openDBs[this.dbInstance] || []).filter(offDB => offDB !== this);
  }

  public static create(dbInstance: OfflineDBInstance, offlineStores: DBOfflineStore<unknown>[], version?: number): Promise<OfflineDB | null> {
    return new Promise((resolve, reject) => {
      let existed = true;
      const request = window.indexedDB.open(dbInstance, version);
      
      request.onerror = (e) => {
        console.error(e);

        reject(e);
      };

      request.onupgradeneeded = (e) => {
        existed = false;

        const db = (e.target as any).result as IDBDatabase;
        
        for (const offStore of offlineStores) {
          if (db.objectStoreNames.contains(offStore.name)) {
            db.deleteObjectStore(offStore.name);
          }
          
          const objectStore = db.createObjectStore(offStore.name, offStore.storeParameters);
          if (offStore.indexes) {
            for (const index of offStore.indexes) {
              objectStore.createIndex(index.name, index.keyPath, index.options);
            }
          }
        }
      };

      request.onsuccess = (e) => {
        const db = (e.target as any).result as IDBDatabase;

        if (existed) {
          // The connection is not handed out to the caller and must not stay
          // open - it would block a later deleteDatabase().
          db.close();

          resolve(null);
          return;
        }

        resolve(new OfflineDB(dbInstance, db));
      };
    });
  }

  /**
   * @returns the existing Database or null if the database did not exist.
   */
  public static open(dbInstance: OfflineDBInstance, version?: number): Promise<OfflineDB | null> {
    return new Promise((resolve, reject) => {
      let existed = true;
      const request = window.indexedDB.open(dbInstance, version);
      
      request.onerror = (e) => {
        console.error(e);

        reject(e);
      };
      request.onupgradeneeded = (e) => { // Gets called for new database, only
        existed = false;
      };
      request.onblocked = (e) => {
        console.error("Cannot open database. Database is blocked!", e);
      }
      request.onsuccess = (e) => {
        const db = (e.target as any).result as IDBDatabase;

        if (!existed) { // New database! Close again and delete it!
          db.close();

          const delReq = window.indexedDB.deleteDatabase(dbInstance);
          delReq.onsuccess = (e) => {
            resolve(null);
          };
          delReq.onerror = (e) => {
            console.error(e);

            reject(e);
          };
          delReq.onblocked = (e) => {
            console.error("Could not delete new created database. Database is blocked 🤨");

            reject(new Error("Database blocked"));
          };

          return;
        }
        
        resolve(new OfflineDB(dbInstance, db));
      };      
    });
  }

  private static deleteDB(dbInstance: OfflineDBInstance, db: IDBDatabase, retryDelete = true): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const request = window.indexedDB.deleteDatabase(dbInstance);
      request.onerror = (e) => {
        console.error(e);

        reject(e);
      };
      request.onblocked = (e) => {
        if (retryDelete) {
          console.warn("Could not delete database. Database is blocked. Trying to close it and delete it again...");
        
          db.close();

          setTimeout(async () => {
            await this.deleteDB(dbInstance, db, false);

            resolve();
          }, 1000);
        } else {
          console.error("Could not delete database. Database is blocked!");

          reject(new Error("Database blocked"));
        }
      }
      request.onsuccess = (e) => {
        resolve();
      };
    });
  }

  public get objectStores(): string[] {
    return Array.from(this.db.objectStoreNames);
  }

  public async deleteDB(): Promise<void> {
    this.close();

    await OfflineDB.deleteDB(this.dbInstance, this.db);
  }

  public add<T>(
    offlineStore: DBOfflineStore<T>, data: T | Unarray<T>,
    multiple = false,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.db.objectStoreNames.contains(offlineStore.name)) {
        reject(new Error("Unknown store: " + offlineStore.name));
      }

      const transaction = this.db.transaction([offlineStore.name], "readwrite");
      transaction.oncomplete = () => {
        resolve();
      };
      transaction.onerror = (e) => {
        console.error(`Cannot add data to store ${offlineStore.name}: ${JSON.stringify(data)}`);
        console.error(e);

        reject(e);
      };

      const objectStore = transaction.objectStore(offlineStore.name);

      if (multiple) {
        for (const item of (data as unknown as Array<any>)) {
          objectStore.add(item);
        }
      } else {
        objectStore.add(data);
      }
    });
  }

  public update<T>(offlineStore: DBOfflineStore<T>, item: Unarray<T>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction([offlineStore.name], "readwrite");
      transaction.oncomplete = () => {
        resolve();
      };
      transaction.onerror = (e) => {
        console.error(`Cannot update data of store ${offlineStore.name}: ${JSON.stringify(item)}`);
        console.error(e);

        reject(e);
      };

      const objectStore = transaction.objectStore(offlineStore.name);
      if (objectStore.keyPath) { // in-line keys: "put" works as update, if object with keyPath (mostly "id") exists already. Otherwise inserts...
        objectStore.put(item);
      } else { // out-of-line keys: delete all and insert afterwards to update...
        const request = objectStore.clear();
        request.onsuccess = (e) => {
          objectStore.add(item);
        };
      }
    });
  }

  public getAll<T>(offlineStore: DBOfflineStore<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const objectStore = this.db.transaction([offlineStore.name], "readonly").objectStore(offlineStore.name);
      const request = objectStore.getAll();
      request.onerror = (e) => {
        console.error(`cannot get all data of store ${offlineStore.name}`);
        console.error(e);

        reject(e);
      };
      request.onsuccess = (e) => {
        resolve(request.result.length === 1 && !objectStore.keyPath ? request.result[0] : request.result);
      };
    });
  }

  public get<T>(offlineStore: DBOfflineStore<T>, keyValue: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const request = this.db.transaction([offlineStore.name], "readonly").objectStore(offlineStore.name).get(keyValue);
      request.onerror = (e) => {
        console.error(`cannot get data for keyValue ${keyValue} in store ${offlineStore.name}`);
        console.error(e);

        reject(e);
      };
      request.onsuccess = (e) => {
        resolve(request.result);
      };
    });
  }

  public getByIndex<T>(offlineStore: DBOfflineStore<T>, index: string, keyValue: string): Promise<T | T[]> {
    return new Promise<T>((resolve, reject) => {
      const request = this.db.transaction([offlineStore.name], "readonly")
        .objectStore(offlineStore.name)
        .index(index)
        .get(keyValue);

      request.onerror = (e)=> {
        console.error(`getByIndex failed for store ${offlineStore.name} and index ${index}`);
        console.error(e);

        reject(e);
      };
      request.onsuccess = (e) => {
        resolve(request.result);
      };
    });
  }

  public delete<T>(offlineStore: DBOfflineStore<T>, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.db.transaction([offlineStore.name], "readwrite")
        .objectStore(offlineStore.name)
        .delete(key);

      request.onerror = (e)=> {
        console.error(`cannot delete key ${key} in ${offlineStore.name}`);
        console.error(e);

        reject(e);
      };
      request.onsuccess = (e) => {
        resolve();
      };
    });
  }
}
